import React, { useState, useEffect, useCallback } from "react";
import { useBuyer } from "../contexts/BuyerContext";
import { useTheme } from "../contexts/ThemeContext";
import Header from "../components/Header";
import PosterGallery from "../components/PosterGallery";
import carddemoProducts from "../carddemo.json";
import "./Settings.css";

const PasscodeModal = ({
  isOpen,
  onClose,
  onSubmit,
  error,
  passcode,
  setPasscode,
}) => {
  if (!isOpen) return null;

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSubmit();
    }
  };

  return (
    <div className="rcm-modal-overlay" onClick={onClose}>
      <div className="rcm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rcm-modal-header">
          <h3>Retail Mode Passcode</h3>
          <button className="rcm-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="rcm-modal-body">
          <div className="rcm-modal-subtitle">
            Enter your 6-digit passcode to disable Retail Customer Mode
          </div>
          {error && <div className="rcm-passkey-error">{error}</div>}
          <div className="rcm-passkey-inputs">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <input
                key={index}
                type="text"
                className="rcm-passkey-input"
                maxLength="1"
                value={passcode[index] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^[0-9]$/.test(value)) {
                    const newPasscode = [...passcode];
                    newPasscode[index] = value === "" ? null : value;
                    const filteredPasscode = newPasscode.filter(
                      (val) => val !== null,
                    );
                    setPasscode(filteredPasscode);

                    if (value !== "" && index < 5) {
                      e.target.nextElementSibling?.focus();
                    }
                  }
                }}
                onKeyDown={handleKeyDown}
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Settings = () => {
  const { buyer, toggleRetailCustomerMode } = useBuyer();
  const { mode, setThemeMode } = useTheme();

  const [retailModeEnabled, setRetailModeEnabled] = useState(
    buyer?.retailCustomerMode || false,
  );
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [passcode, setPasscode] = useState([]);
  const [passcodeError, setPasscodeError] = useState("");

  useEffect(() => {
    setRetailModeEnabled(buyer?.retailCustomerMode || false);
  }, [buyer?.retailCustomerMode]);

  const handleRetailModeToggle = async (e) => {
    const isChecked = e.target.checked;

    if (!isChecked && buyer?.privatePasskey?.length > 0) {
      setShowPasscodeModal(true);
      setPasscode([]);
      setPasscodeError("");
      return;
    }

    setRetailModeEnabled(isChecked);
    toggleRetailCustomerMode(isChecked);
  };

  const handlePasscodeSubmit = useCallback(() => {
    const enteredPasscode = passcode.join("");
    const storedPasscode = buyer?.privatePasskey?.join("");

    if (passcode.length !== 6) {
      setPasscodeError("Passcode must be exactly 6 digits");
      return;
    }

    if (enteredPasscode === storedPasscode) {
      setRetailModeEnabled(false);
      toggleRetailCustomerMode(false);
      setShowPasscodeModal(false);
      setPasscode([]);
    } else {
      setPasscodeError("Invalid passcode");
      setTimeout(() => {
        setShowPasscodeModal(false);
        setPasscode([]);
        setPasscodeError("");
      }, 1500);
    }
  }, [passcode, buyer?.privatePasskey, toggleRetailCustomerMode]);

  // Auto-check passcode when 6 digits are entered
  useEffect(() => {
    if (passcode.length === 6 && showPasscodeModal) {
      handlePasscodeSubmit();
    }
  }, [passcode, showPasscodeModal, handlePasscodeSubmit]);

  const handlePasscodeCancel = () => {
    setShowPasscodeModal(false);
    setPasscode([]);
    setPasscodeError("");
  };

  const metals = [
    {
      id: "gold",
      name: "Gold",
      icon: "💛",
      color: "#D4AF37",
      minPercentage: 5,
    },
    {
      id: "silver",
      name: "Silver",
      icon: "🪙",
      color: "#C0C0C0",
      minPercentage: 5,
    },
    {
      id: "diamond",
      name: "Diamond",
      icon: "💎",
      color: "#B9F2FF",
      minPercentage: 10,
    },
    {
      id: "platinum",
      name: "Platinum",
      icon: "💙",
      color: "#E5E4E2",
      minPercentage: 10,
    },
    {
      id: "gemstone",
      name: "Gemstone",
      icon: "🌈",
      color: "#8A2BE2",
      minPercentage: 5,
    },
  ];

  const [segmentErrors, setSegmentErrors] = useState({});

  const defaultCardTemplate = {
    storeName: buyer?.organizationName || "JMI Jewellers",
    storeNameLocked: !!buyer?.organizationName,
    logo: buyer?.logoUrl || "",
    logoLocked: !!buyer?.logoUrl,
    showLogo: true,
    showWatermark: true,
    watermarkPosition: "bottom-right",
    watermarkStyle: "badge",
    cardStyle: "modern",
    cardBgColor: "#ffffff",
    cardTextColor: "#1a1f36",
    accentColor: "#D4AF37",
    cardBorderRadius: 16,
    cardPadding: 24,
    productImage: "",
    showContact: true,
    showAddress: false,
    contactInfo: "",
    address: "",
    showPrice: true,
    showPurity: true,
    showWeight: true,
    layoutType: "catalogue",
    jmiWatermark: true,
    catalogueTheme: "pearl",
    activeTheme: "light-studio",
    fieldsToShow: {
      pricing: {
        retail_price: true,
        retail_margin_percent: true,
      },
      attributes: {
        purity_karat: true,
        weight_grams: true,
        finish: true,
        hallmarked: true,
        gender: true,
      },
    },
    metalValues: {
      gold: { percentage: 5 },
      silver: { percentage: 5 },
      diamond: { percentage: 10 },
      platinum: { percentage: 10 },
      gemstone: { percentage: 5 },
    },
  };

  const [cardDesign, setCardDesign] = useState(() => {
    if (buyer?.cardDesign) {
      const migrated = { ...buyer.cardDesign };
      if (migrated.metalValues) {
        migrated.metalValues = Object.keys(migrated.metalValues).reduce(
          (acc, id) => {
            const mv = migrated.metalValues[id];
            if (typeof mv.percentage === "string") {
              acc[id] = { percentage: parseInt(mv.percentage) || 5 };
            } else {
              acc[id] = { percentage: mv.percentage || 5 };
            }
            return acc;
          },
          {},
        );
      }
      if (!migrated.fieldsToShow) {
        migrated.fieldsToShow = defaultCardTemplate.fieldsToShow;
      }
      return { ...defaultCardTemplate, ...migrated };
    }
    return defaultCardTemplate;
  });

  const handleSegmentPercentageChange = (metal, value) => {
    const numValue = parseInt(value) || 0;
    const min = metals.find((m) => m.id === metal)?.minPercentage || 5;
    if (numValue < min) {
      setSegmentErrors((prev) => ({
        ...prev,
        [metal]: `Minimum ${min}% required`,
      }));
      return;
    }
    setSegmentErrors((prev) => ({ ...prev, [metal]: "" }));
    setCardDesign((prev) => ({
      ...prev,
      metalValues: {
        ...prev.metalValues,
        [metal]: {
          ...prev.metalValues[metal],
          percentage: numValue,
        },
      },
    }));
  };

  const handlePresetChipClick = (metal, value) => {
    handleSegmentPercentageChange(metal, value.toString());
  };

  const handleInputIncrement = (metal) => {
    const currentValue = cardDesign.metalValues[metal].percentage;
    const max = 100;
    if (currentValue < max) {
      handleSegmentPercentageChange(metal, (currentValue + 1).toString());
    }
  };

  const handleInputDecrement = (metal) => {
    const currentValue = cardDesign.metalValues[metal].percentage;
    if (currentValue > 0) {
      handleSegmentPercentageChange(metal, (currentValue - 1).toString());
    }
  };

  const presetChips = [5, 10, 25];

  return (
    <>
      <Header title={"Setting"} />
      <div className="settings-page">
        <div className="settings-header">
          <h1>Settings</h1>
          <p>Configure your retail margins and preferences</p>
        </div>

        <div className="settings-content">
          <div className="retail-mode-card">
            <div className="retail-mode-card-header">
              <div className="retail-mode-info">
                <div className="retail-mode-icon">🛍️</div>
                <div className="retail-mode-text">
                  <h3>Retail Customer Mode</h3>
                  <p>Display retail prices with your profit margin included</p>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={retailModeEnabled}
                  onChange={handleRetailModeToggle}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
            <div className="retail-mode-status-line">
              <span className="status-indicator">
                <span className="status-dot"></span>
                <span className={retailModeEnabled ? "status-on" : "status-off"}>
                  {retailModeEnabled ? "Enabled" : "Disabled"}
                </span>
              </span>
              <span className="retail-mode-hint">
                {retailModeEnabled
                  ? "showing retail prices"
                  : "showing wholesale prices"}
              </span>
            </div>
            <div className="retail-mode-details">
              <div className="retail-mode-status">
                <span className="status-dot"></span>
                <span>Active status</span>
              </div>
              <div className="retail-mode-hint">
                💡 Toggle to switch between wholesale and retail pricing
              </div>
            </div>
          </div>
           {showPasscodeModal && (
            <PasscodeModal
              isOpen={showPasscodeModal}
              onClose={handlePasscodeCancel}
              onSubmit={handlePasscodeSubmit}
              error={passcodeError}
              passcode={passcode}
              setPasscode={setPasscode}
            />
          )}

          <div className="theme-switcher-card">
            <div className="theme-switcher-header">
              <div className="theme-switcher-info">
                <div className="theme-switcher-icon">🎨</div>
                <div className="theme-switcher-text">
                  <h3>Theme</h3>
                  <p>Choose your preferred color scheme</p>
                </div>
              </div>
              <div className="theme-toggle-group">
                <button
                  type="button"
                  className={`theme-toggle-btn ${mode === "light" ? "active" : ""}`}
                  onClick={() => setThemeMode("light")}
                >
                  Light
                </button>
                <button
                  type="button"
                  className={`theme-toggle-btn ${mode === "dark" ? "active" : ""}`}
                  onClick={() => setThemeMode("dark")}
                >
                  Dark
                </button>
              </div>
            </div>
            <div className="theme-switcher-status-line">
              <span className="status-indicator">
                <span className="status-dot"></span>
                <span className={mode === "light" ? "status-on" : "status-on"}>
                  {mode === "light" ? "Light mode active" : "Dark mode active"}
                </span>
              </span>
              <span className="theme-switcher-hint">
                {mode === "light"
                  ? "using light theme"
                  : "using dark theme"}
              </span>
            </div>
          </div>

          <div className="settings-section">
            <h2>Segment Retail Margins</h2>

            <div className="metal-cards-swiper">
              {metals.map((metal) => (
                <div
                  key={metal.id}
                  className="metal-card-compact"
                >
                  <div className="metal-card-row">
                    <span className="metal-name">{metal.name}</span>
                    <div> <div className="metal-chips-row">
                      {presetChips.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          className={`metal-chip ${cardDesign.metalValues[metal.id].percentage ===
                              preset
                              ? "active"
                              : ""
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePresetChipClick(metal.id, preset);
                          }}
                          disabled={preset < metal.minPercentage}
                        >
                          {preset}%
                        </button>
                      ))}
                    </div>
                      <div className="metal-inline-controls">
                        <button
                          type="button"
                          className="input-step-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInputDecrement(metal.id);
                          }}
                          aria-label={`Decrease ${metal.name} percentage`}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min={metal.minPercentage}
                          step="1"
                          value={cardDesign.metalValues[metal.id].percentage}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSegmentPercentageChange(
                              metal.id,
                              e.target.value,
                            );
                          }}
                          onBlur={(e) => {
                            e.stopPropagation();
                            const val = e.target.value;
                            if (val === "" || isNaN(parseInt(val))) {
                              handleSegmentPercentageChange(
                                metal.id,
                                metal.minPercentage.toString(),
                              );
                            }
                          }}
                          onFocus={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          className="form-input percentage-input"
                          placeholder={`Min ${metal.minPercentage}`}
                          title={`Minimum ${metal.minPercentage}%`}
                          style={{ width: "60px" }}
                        />
                        <button
                          type="button"
                          className="input-step-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInputIncrement(metal.id);
                          }}
                          aria-label={`Increase ${metal.name} percentage`}
                        >
                          +
                        </button>
                        <span className="input-suffix">%</span>
                      </div></div>
                  </div>

                  {segmentErrors[metal.id] && (
                    <span className="field-error">
                      {segmentErrors[metal.id]}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      <div className="settings-section">
        <PosterGallery buyer={buyer} products={carddemoProducts} />
      </div>

    </>
  );
};

export default Settings;
